// This optional code is used to register a service worker.
// register() is not called by default.
//
// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.
//
// To learn more about the benefits of this model and instructions on how to
// opt-in, read https://cra.link/PWA

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
  // [::1] is the IPv6 localhost address.
  window.location.hostname === '[::1]' ||
  // 127.0.0.0/8 are considered localhost for IPv4.
  window.location.hostname.match(
    /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
  )
);

export function register(config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      // service worker won't work if PUBLIC_URL is on a different origin
      return;
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      if (isLocalhost) {
        // Validate on localhost first
        checkValidServiceWorker(swUrl, config);
        navigator.serviceWorker.ready.then(() => {
          console.log(
            'This web app is being served cache-first by a service ' +
            'worker. To learn more, visit https://cra.link/PWA'
          );
        });
      } else {
        // Register for production
        registerValidSW(swUrl, config);
      }
    });
  }
}

/*
The worker calls skipWaiting() and clientsClaim(), so a new deployment takes
control of pages that are still running the PREVIOUS bundle. Any chunk the page
lazy-loads after that point comes from the new precache, and mixing the two
module graphs surfaces as "Minified React error #130" on navigation.

Reloading once, the moment the new worker takes over, guarantees the page's
JavaScript matches the cache serving it. The listener is only attached when a
controller already exists, so a first-time install does not trigger a reload.
*/
let reloading = false;
const reloadOnce = () => {
  if (reloading) return;
  reloading = true;
  window.location.reload();
};

const reloadOnControllerChange = () => {
  if (!navigator.serviceWorker.controller) return;
  navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
};

const UPDATE_TIMEOUT_MS = 15000;

/*
Called by the new-version banner's Refresh button. A plain reload is answered by
the OLD worker's precached index.html, so the page comes back on the old build
and only reaches the new one after the worker swap forces a second reload.
Fetching the new worker first and reloading once it takes control lands on the
new build in a single reload. Falls back to a plain reload when there is no new
worker to wait for, or it fails to install or stalls.
*/
export async function applyUpdate() {
  const registration = await navigator.serviceWorker?.getRegistration();
  if (!registration) return reloadOnce();

  navigator.serviceWorker.addEventListener('controllerchange', reloadOnce);
  setTimeout(reloadOnce, UPDATE_TIMEOUT_MS);
  try {
    await registration.update();
  } catch {
    return reloadOnce();
  }

  const worker = registration.installing || registration.waiting;
  if (!worker) return reloadOnce();
  worker.addEventListener('statechange', () => {
    if (worker.state === 'redundant') reloadOnce();
  });
}

function registerValidSW(swUrl, config) {
  reloadOnControllerChange();
  navigator.serviceWorker
  .register(swUrl)
  .then((registration) => {
    registration.onupdatefound = () => {
      const installingWorker = registration.installing;
      if (!installingWorker) {
        return;
      }
      installingWorker.onstatechange = () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // Activate the new SW immediately so it takes over the cache.
            // controllerchange reloads pages that loaded under the old worker.
            console.log('New content is available — activating SW and prompting user to refresh.');
            installingWorker.postMessage({ type: 'SKIP_WAITING' });

            if (config && config.onUpdate) {
              config.onUpdate(registration);
            }
          } else {
            // First install: everything is cached
            console.log('Content is cached for offline use.');
            if (config && config.onSuccess) {
              config.onSuccess(registration);
            }
          }
        }
      };
    };
  })
  .catch((error) => {
    console.error('Error during service worker registration:', error);
  });
}

function checkValidServiceWorker(swUrl, config) {
  // Check if the service worker can be found. If it can't reload the page.
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
  .then((response) => {
    const contentType = response.headers.get('content-type');
    if (
      response.status === 404 ||
      (contentType != null && contentType.indexOf('javascript') === -1)
    ) {
      // No valid SW found, unregister and reload
      navigator.serviceWorker.ready.then((registration) => {
        registration.unregister().then(() => {
          window.location.reload();
        });
      });
    } else {
      // SW found, proceed with normal registration
      registerValidSW(swUrl, config);
    }
  })
  .catch(() => {
    console.log(
      'No internet connection found. App is running in offline mode.'
    );
  });
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
    .then((registration) => {
      registration.unregister();
    })
    .catch((error) => {
      console.error(error.message);
    });
  }
}